import { renderImageEdit } from '@buffer-mono/popcorn'
import type {
  ImageEdit,
  ImageOutputMimeType,
  RenderedImageEdit,
} from '@buffer-mono/popcorn'

const IMAGE_RENDER_QUALITY = 0.9
const IMAGE_MINIMUM_QUALITY = 0.5
const IMAGE_MAX_COMPRESSION_ATTEMPTS = 4

export type ImageOutputLimits = Readonly<{
  maxBytes: number | null
  maxDimension: number
  maxPixels: number
  mimeType: ImageOutputMimeType
}>

export const renderImageWithinLimits = async ({
  edit,
  limits,
  renderImage,
  attempt = 0,
}: Readonly<{
  edit: ImageEdit
  limits: ImageOutputLimits
  renderImage: HTMLImageElement
  attempt?: number
}>): Promise<RenderedImageEdit> => {
  const quality =
    limits.mimeType === 'image/png'
      ? undefined
      : Math.max(IMAGE_MINIMUM_QUALITY, IMAGE_RENDER_QUALITY - attempt * 0.1)
  const renderedImage = await renderImageEdit(renderImage, edit, {
    mimeType: limits.mimeType,
    maxDimension: limits.maxDimension,
    maxPixels: limits.maxPixels,
    quality,
    resizeToFitLimits: true,
  })

  if (limits.maxBytes === null || renderedImage.blob.size <= limits.maxBytes) {
    return renderedImage
  }

  if (attempt >= IMAGE_MAX_COMPRESSION_ATTEMPTS) {
    throw new Error('The image could not be compressed to the required size.')
  }

  const nextMaxPixels = Math.max(
    1,
    Math.floor(
      renderedImage.width *
        renderedImage.height *
        (limits.maxBytes / renderedImage.blob.size) *
        0.9,
    ),
  )

  return renderImageWithinLimits({
    edit,
    limits: {
      ...limits,
      maxPixels: Math.min(limits.maxPixels, nextMaxPixels),
    },
    renderImage,
    attempt: attempt + 1,
  })
}
